import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Check, ArrowRight, Zap, Star, Shield, AlertCircle, Crown, Package, Calendar, Gift } from 'lucide-react';

const calcDiscount = (original, current) => {
  if (!original || !current || original <= current) return 0;
  return Math.round(((original - current) / original) * 100);
};

const PricingPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [currentSub, setCurrentSub] = useState(null);
  const [packCount, setPackCount] = useState(1);
  const [cmsContent, setCmsContent] = useState({});

  useEffect(() => {
    axios.get(`${API}/subscription/plans`)
      .then(res => setPlans(res.data.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    axios.get(`${API}/settings/public`)
      .then(res => setCmsContent(res.data?.pricing || {}))
      .catch(() => {});
    if (token) {
      const headers = { Authorization: `Bearer ${token}` };
      axios.get(`${API}/subscription/status`, { headers, withCredentials: true })
        .then(res => setCurrentSub(res.data.subscription))
        .catch(() => {});
    }
  }, [token]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubscribe = async (planId) => {
    if (!user) { navigate('/register'); return; }
    if (user.role === 'candidate') { toast.error('Subscription plans are for institutions only'); return; }

    setProcessingPlan(planId);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/subscription/create-order`,
        { plan_id: planId, pack_count: planId === 'pack' ? packCount : 1 },
        { headers, withCredentials: true }
      );

      if (res.data.status === 'free') {
        toast.success(res.data.message);
        navigate('/dashboard');
        return;
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) { toast.error('Failed to load payment gateway'); setProcessingPlan(null); return; }

      const options = {
        key: res.data.key_id,
        amount: res.data.amount,
        currency: res.data.currency,
        name: 'EdAssist',
        description: `${planId === 'pack' ? `Pack of ${packCount} Listings` : planId === 'monthly' ? '1 Month Plan' : '1 Year Plan'}`,
        order_id: res.data.order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/subscription/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
            toast.success('Payment successful! Subscription activated.');
            navigate('/dashboard');
          } catch { toast.error('Payment verification failed.'); }
        },
        prefill: { email: user?.email, name: user?.name },
        theme: { color: '#4f46e5' },
        modal: { ondismiss: () => setProcessingPlan(null) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(detail.includes('not configured') ? 'Payment gateway not yet configured. Contact admin.' : detail);
      setProcessingPlan(null);
    }
  };

  const freePlan = plans.find(p => p.plan_id === 'free');
  const packPlan = plans.find(p => p.plan_id === 'pack');
  const monthlyPlan = plans.find(p => p.plan_id === 'monthly');
  const yearlyPlan = plans.find(p => p.plan_id === 'yearly');

  const headline = cmsContent.headline || 'Go Premium';
  const subheadline = cmsContent.subheadline || 'Hire Top Educators, Faster';
  const description = cmsContent.description || 'AI-powered tools, more applications, and dedicated support — built for serious hiring.';

  // Parse amounts for discount calculation (amounts are in paise)
  const monthlyOriginal = monthlyPlan?.original_amount ? monthlyPlan.original_amount / 100 : null;
  const monthlyPrice = monthlyPlan?.amount ? monthlyPlan.amount / 100 : null;
  const yearlyOriginal = yearlyPlan?.original_amount ? yearlyPlan.original_amount / 100 : null;
  const yearlyPrice = yearlyPlan?.amount ? yearlyPlan.amount / 100 : null;
  const monthlyDiscount = calcDiscount(monthlyOriginal, monthlyPrice);
  const yearlyDiscount = calcDiscount(yearlyOriginal, yearlyPrice);

  return (
    <PublicLayout>
      <SEO title="Pricing" description="Affordable pricing plans for schools and institutions. Post teaching jobs, access AI-powered tools, and find the best educators. Start free." path="/pricing" />
      <div className="min-h-screen" data-testid="pricing-page">
        {/* Hero */}
        <section className="bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-900 text-white py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">For Institutions</Badge>
            <h1 className="text-3xl lg:text-5xl font-bold mb-3" style={{ fontFamily: 'Outfit' }} data-testid="pricing-headline">
              {headline}
            </h1>
            <p className="text-xl lg:text-2xl text-indigo-100 mb-2" data-testid="pricing-subheadline">{subheadline}</p>
            <p className="text-indigo-200 max-w-xl mx-auto">{description}</p>

            {/* Stats */}
            <div className="flex justify-center gap-10 mt-8 pt-6 border-t border-white/10">
              <div>
                <p className="text-3xl font-bold text-white">100+</p>
                <p className="text-sm text-indigo-200">Schools & Institutions</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-white">10,000+</p>
                <p className="text-sm text-indigo-200">Educators Registered</p>
              </div>
            </div>
            {currentSub && (
              <div className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-lg px-4 py-2">
                <Shield className="w-4 h-4 text-green-300" />
                <span className="text-sm">Current plan: <strong>{currentSub.name}</strong></span>
              </div>
            )}
          </div>
        </section>

        {/* Free Tier — Highlighted */}
        {freePlan && (
          <section className="relative -mt-8 z-10 px-4">
            <div className="max-w-3xl mx-auto">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl shadow-xl p-1">
                <div className="bg-white rounded-xl p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-5">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Gift className="w-7 h-7 text-emerald-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Start Free — ₹0</h3>
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-bold">NO CREDIT CARD</Badge>
                        </div>
                        <p className="text-slate-600 text-sm">Post <strong>1 Job every month</strong> at zero cost. Includes basic ATS, candidate applications, and profile access.</p>
                      </div>
                    </div>
                    <Button
                      size="lg"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0 px-8 shadow-lg shadow-emerald-200"
                      onClick={() => handleSubscribe('free')}
                      disabled={processingPlan === 'free' || currentSub?.tier === 'free'}
                      data-testid="subscribe-free-btn"
                    >
                      {currentSub?.tier === 'free' ? 'Current Plan' : 'Get Started Free'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Pricing Cards */}
        <section className="py-12 lg:py-16">
          <div className="max-w-6xl mx-auto px-4">
            {loading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto" /></div>
            ) : (
              <div className="grid md:grid-cols-3 gap-6 lg:gap-8">

                {/* Pack of Listings */}
                {packPlan && (
                  <div className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow" data-testid="pricing-card-pack">
                    <div className="bg-slate-50 px-6 py-4 border-b">
                      <div className="flex items-center gap-2">
                        <Package className="w-5 h-5 text-indigo-600" />
                        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{packPlan.name}</h3>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">Choose based on your hiring needs</p>
                    </div>
                    <div className="px-6 py-6 space-y-5">
                      <div>
                        <Badge className="bg-blue-100 text-blue-700 text-xs mb-3">VALID FOR 1 YEAR</Badge>
                        <Select value={String(packCount)} onValueChange={(v) => setPackCount(Number(v))}>
                          <SelectTrigger className="w-full" data-testid="pack-count-select">
                            <SelectValue placeholder="Select listings" />
                          </SelectTrigger>
                          <SelectContent>
                            {(packPlan.pack_options || [1,3,5,10,20]).map(n => (
                              <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'LISTING' : 'LISTINGS'}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-slate-900">₹{((packPlan.amount_per_listing || 59900) * packCount / 100).toLocaleString()}</span>
                        </div>
                      </div>
                      <ul className="space-y-2 pt-2">
                        {(packPlan.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => handleSubscribe('pack')}
                        disabled={processingPlan === 'pack'}
                        data-testid="subscribe-pack-btn"
                      >
                        {processingPlan === 'pack' ? 'Processing...' : `Buy ${packCount} listing${packCount > 1 ? 's' : ''}`}
                      </Button>
                    </div>
                  </div>
                )}

                {/* 1 Month Plan */}
                {monthlyPlan && (
                  <div className="relative bg-white border-2 border-indigo-500 rounded-2xl overflow-hidden shadow-lg" data-testid="pricing-card-monthly">
                    <div className="bg-indigo-600 px-6 py-4 text-white">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>{monthlyPlan.name}</h3>
                      </div>
                      <p className="text-sm text-indigo-100 mt-1">Post unlimited premium internships and jobs</p>
                    </div>
                    <div className="px-6 py-6 space-y-5">
                      <div>
                        <Badge className="bg-indigo-100 text-indigo-700 text-xs mb-3">UNLIMITED PLAN</Badge>
                        {monthlyDiscount > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs ml-2 mb-3">{monthlyDiscount}% OFF</Badge>
                        )}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-slate-900">{monthlyPlan.display_price}</span>
                          {monthlyOriginal && monthlyOriginal > (monthlyPrice || 0) && (
                            <span className="text-lg text-slate-400 line-through">₹{monthlyOriginal.toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <ul className="space-y-2 pt-2">
                        {(monthlyPlan.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => handleSubscribe('monthly')}
                        disabled={processingPlan === 'monthly' || currentSub?.tier === 'monthly'}
                        data-testid="subscribe-monthly-btn"
                      >
                        {currentSub?.tier === 'monthly' ? 'Current Plan' : processingPlan === 'monthly' ? 'Processing...' : 'Buy 1 Month Plan'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* 1 Year Plan */}
                {yearlyPlan && (
                  <div className="relative bg-white border-2 border-amber-400 rounded-2xl overflow-hidden shadow-lg" data-testid="pricing-card-yearly">
                    {yearlyPlan.badge && (
                      <div className="absolute top-0 right-0">
                        <Badge className="bg-amber-400 text-amber-900 rounded-none rounded-bl-lg px-3 py-1.5 text-xs font-bold">
                          <Crown className="w-3 h-3 mr-1" />{yearlyPlan.badge}
                        </Badge>
                      </div>
                    )}
                    <div className="bg-slate-900 px-6 py-4 text-white">
                      <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-amber-400" />
                        <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>{yearlyPlan.name}</h3>
                      </div>
                      <p className="text-sm text-slate-300 mt-1">Post unlimited premium internships and jobs</p>
                    </div>
                    <div className="px-6 py-6 space-y-5">
                      <div>
                        <Badge className="bg-slate-100 text-slate-700 text-xs mb-3">UNLIMITED PLAN</Badge>
                        {yearlyDiscount > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs ml-2 mb-3">{yearlyDiscount}% OFF</Badge>
                        )}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-slate-900">{yearlyPlan.display_price}</span>
                          {yearlyOriginal && yearlyOriginal > (yearlyPrice || 0) && (
                            <span className="text-lg text-slate-400 line-through">₹{yearlyOriginal.toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <ul className="space-y-2 pt-2">
                        {(yearlyPlan.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />{f}
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full bg-slate-900 hover:bg-slate-800"
                        onClick={() => handleSubscribe('yearly')}
                        disabled={processingPlan === 'yearly' || currentSub?.tier === 'yearly'}
                        data-testid="subscribe-yearly-btn"
                      >
                        {currentSub?.tier === 'yearly' ? 'Current Plan' : processingPlan === 'yearly' ? 'Processing...' : 'Buy 1 Year Plan'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Note */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                <AlertCircle className="w-4 h-4" />
                Payments securely processed via Razorpay. All amounts in INR.
              </div>
            </div>
          </div>
        </section>

        {/* Assisted & Customized Plan */}
        <section className="py-12 bg-slate-50">
          <div className="max-w-3xl mx-auto px-4">
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-2xl p-8 lg:p-10 shadow-sm">
              <h3 className="text-2xl lg:text-3xl font-bold text-blue-900 mb-2" style={{ fontFamily: 'Outfit' }}>Assisted and Customized Plan</h3>
              <p className="font-semibold text-slate-800 mb-4">Streamline Your Hiring with Expert Support from EdAssist</p>
              <p className="text-slate-600 mb-6">Let EdAssist help you source, screen, and select the right candidates — efficiently and effectively.</p>

              <ul className="space-y-4 mb-8">
                {[
                  '1:1 Consultation with a dedicated hiring expert',
                  'Professionally drafted job post on EdAssist',
                  'Sourcing & shortlisting of up to 10 highly relevant candidates',
                  'End-to-end support through interviews and onboarding',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0 bg-teal-50 rounded" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>

              <p className="font-semibold text-slate-800 mb-4">Price and scope customizable based on your institution's needs</p>
              <p className="text-slate-600">
                📩 Contact us at <a href="mailto:info@edassist.co.in" className="text-blue-600 font-medium underline">info@edassist.co.in</a> for this plan
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Outfit' }}>Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { q: "How does the Free plan work?", a: "You get 1 job posting per month at zero cost. The listing renews monthly." },
                { q: "What is a Pack of Listings?", a: `Buy a set number of job listings at ₹${((packPlan?.amount_per_listing || 59900) / 100).toLocaleString()} each. They're valid for 1 year from purchase.` },
                { q: "What payment methods are accepted?", a: "UPI, Net Banking, Credit/Debit Cards, and Wallets through Razorpay." },
                { q: "Can I upgrade later?", a: "Yes! You can switch to a higher plan anytime. Your unused pack listings carry forward." },
              ].map((faq, idx) => (
                <div key={idx} className="bg-white rounded-xl p-6 border border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-slate-900 text-white text-center">
          <div className="max-w-xl mx-auto px-4">
            <Zap className="w-12 h-12 text-indigo-400 mx-auto mb-4" />
            <h2 className="text-2xl lg:text-3xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>Ready to Transform Your Hiring?</h2>
            <p className="text-slate-300 mb-8">Join hundreds of institutions using EdAssist to find top educators.</p>
            <Link to="/register">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100">Start Free<ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
};

export default PricingPage;
